import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid } from '@mui/material';
import { MarketLink } from '@market/react';
import { toCurrency, toCurrencyWithMinorUnitAmount } from 'utilities/currency';
import ActionablePromotion from 'components/common/receipt/actionable_promotion';
import ReceiptItem from 'components/common/receipt/receipt_item';
import HardwareRentalSection from 'components/common/receipt/hardware_rental_section';
import IssuerInstallmentsPrice from 'components/common/receipt/issuer_installments_price';
import TaxReceiptItem from 'components/common/receipt/tax_receipt_item';
import FinancingModal from 'components/pages/product/financing_modal';
import PageLoader from 'components/common/page_loader';
import PromoAdd from 'components/common/promo_add';
import {
  getSoftwarePriceLabel,
  getKioskPnpBreakdown,
  getKioskPricingContext,
  sumSoftwarePricing,
  sortProductByHardwareOrSoftware,
} from 'utilities/software';
import { BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY } from 'constants/software';
import { FOOTNOTE_SYMBOL } from 'components/common/banners/banner_helpers';
import { useFeatureFlags } from 'components/context/feature_flags';
import { formatTierMonthlyPrice } from './bundle_offer_utils';

const getBundleTierKey = (product) =>
  BUNDLE_SQUARE_TIER_BY_BASE_PRODUCT_KEY[product?.base_product_key] || null;

const getBundleTier = (orderData, product) => {
  const tierKey = getBundleTierKey(product);
  if (!tierKey) return null;
  return orderData?.hardware_bundle_offer?.tiers?.[tierKey] || null;
};

const getBundleTierPriceLabel = ({ tier, t }) => {
  const price = formatTierMonthlyPrice(tier);
  if (!price) return null;

  return `${t('cart.bundleOffer.priceOnly', { price })} ${t(
    'cart.bundleOffer.comparePlans.perLocation',
  )}`;
};

const CartReceipt = ({
  loading = false,
  orderData,
  financeData,
  cartClient,
  orderRefresh,
}) => {
  const { t } = useTranslation();
  const { s3bKioskSoftwareRenameToKioskLite: kioskLiteEnabled } =
    useFeatureFlags();
  const [disabled, setDisabled] = useState(false);
  const [viewFinance, setViewFinance] = useState(false);

  const { software: softwareProducts } = sortProductByHardwareOrSoftware(
    orderData.cart_summary,
  );
  const softwareReceiptItems = softwareProducts.map((product) => ({
    product,
    bundleTierKey: getBundleTierKey(product),
    bundleTier: getBundleTier(orderData, product),
  }));

  const { isPnpSubscribed, locationCount, kioskDeviceCount } =
    getKioskPricingContext(orderData);

  const nonBundleSoftwareSubtotal = sumSoftwarePricing(
    softwareReceiptItems
      .filter(({ bundleTierKey, bundleTier }) => !bundleTierKey || !bundleTier)
      .map(({ product }) => product.plan)
      .filter((product) => product !== null),
    {
      isPnpSubscribed,
      locationCount,
      kioskDeviceCount,
    },
  );
  const bundleSoftwareCostTotal = softwareReceiptItems.reduce(
    (total, { bundleTier }) => total + (bundleTier?.monthly_cents ?? 0),
    0,
  );
  const bundleSoftwareCurrency = softwareReceiptItems
    .map(({ bundleTier }) => bundleTier?.currency)
    .find(Boolean);
  const softwareCostTotal =
    nonBundleSoftwareSubtotal.amount + bundleSoftwareCostTotal;
  const softwareCurrency =
    bundleSoftwareCurrency || nonBundleSoftwareSubtotal.currencyCode;

  const removePromotion = (promoCode) => {
    setDisabled(true);
    cartClient
      .removePromotion(promoCode)
      .then(orderRefresh)
      .finally(() => setDisabled(false));
  };

  const isReadOnly = Boolean(orderData.is_hardware_rental_order);

  const promotions = orderData.discounts.map((promo, index) => (
    <ActionablePromotion
      key={`discount-${index}`}
      disabled={disabled}
      code={promo.code}
      adjustments={promo.adjustments}
      currency={promo.currency}
      canRemove={!promo.auto_applied && !isReadOnly}
      didReachCartLimit={promo.limit_reached}
      onRemove={removePromotion}
    />
  ));

  if (loading) {
    return <PageLoader />;
  }

  return (
    <Grid
      container
      direction="column"
      sx={{
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1.5,
        boxSizing: 'border-box',
        py: 4,
      }}
    >
      {/* Hardware Section */}
      <Box
        sx={{
          px: 3,
        }}
      >
        <Box
          sx={{
            mb: 2,
          }}
        >
          <b className="semibold-20">{t('common.hardware').toUpperCase()}</b>
        </Box>
        <ReceiptItem
          label={'checkout.receipt.label.subtotal'}
          cost={orderData.subtotal.amount}
          currency={orderData.subtotal.currency}
        />
        <ReceiptItem
          label={orderData.shipping.label}
          cost={orderData.shipping.amount}
          currency={orderData.shipping.currency}
          isShipping={true}
        />
        <TaxReceiptItem orderData={orderData} />
        {promotions.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            {promotions}
          </>
        )}
        <Divider sx={{ my: 2 }} />
        <ReceiptItem
          label={orderData.total.label}
          cost={orderData.total.amount}
          currency={orderData.total.currency}
          topOffset={1}
          bottomOffset={1}
          emphasize
        />
        {orderData.total.amount > 0 && financeData && (
          <MarketLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setViewFinance(true);
            }}
            className="medium-20"
            style={{ textAlign: 'right' }}
          >
            {`${t('productPage.financingModalTrigger', {
              price: toCurrency(financeData.amount, financeData.currency),
              period: financeData.number_payment_periods,
            })}${FOOTNOTE_SYMBOL.oneSuperscript}`}
          </MarketLink>
        )}
        {orderData.issuer_installments_summary?.quote_payments_amount && (
          <IssuerInstallmentsPrice
            issuerInstallmentsDetails={orderData.issuer_installments_summary}
          />
        )}
        {!isReadOnly && (
          <>
            <Divider sx={{ my: 2 }} />
            <PromoAdd cartClient={cartClient} orderRefresh={orderRefresh} />
          </>
        )}
        <FinancingModal
          isLoggedIn={orderData.current_user.is_logged_in}
          open={viewFinance}
          handleClose={() => setViewFinance(false)}
        />
      </Box>
      {/* Hardware Services (rental subscription) Section */}
      {orderData.hardware_rental_summary?.items?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ px: 3 }}>
            <HardwareRentalSection
              hardwareRentalSummary={orderData.hardware_rental_summary}
            />
          </Box>
        </>
      )}
      {/* Software Section */}
      {softwareProducts.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              px: 3,
            }}
          >
            <Box
              sx={{
                my: 2,
              }}
            >
              <b className="semibold-20">
                {t('common.software').toUpperCase()}
              </b>
            </Box>
            {softwareReceiptItems.flatMap(({ product, bundleTier }) => {
              if (bundleTier) {
                return (
                  <ReceiptItem
                    key={product.id}
                    label={bundleTier.label || product.product_variant.title}
                    override={getBundleTierPriceLabel({ tier: bundleTier, t })}
                  />
                );
              }

              const breakdown = getKioskPnpBreakdown({
                plan: product?.plan,
                kioskLiteEnabled,
                t,
                isPnpSubscribed,
              });

              if (breakdown) {
                return breakdown.map((line, index) => (
                  <ReceiptItem
                    key={`${product.id}-${index}`}
                    label={line.name}
                    override={line.receiptLabel}
                  />
                ));
              }

              return (
                <ReceiptItem
                  key={product.id}
                  label={product.product_variant.title}
                  override={getSoftwarePriceLabel({
                    plan: product?.plan,
                    t,
                    isPnpSubscribed,
                    locationCount,
                    kioskDeviceCount,
                  })}
                />
              );
            })}
            <Divider sx={{ my: 2 }} />
            <ReceiptItem
              label={t('checkout.receipt.label.estimatedMonthlyBill')}
              override={toCurrencyWithMinorUnitAmount(
                softwareCostTotal,
                softwareCurrency,
              )}
              emphasize
            />
            <Box mt={2}>
              <p className="paragraph-20">
                {t('checkout.receipt.label.softwareBillingDisclaimer')}
              </p>
            </Box>
          </Box>
        </>
      )}
    </Grid>
  );
};

export default CartReceipt;
